import type { TruckServiceItemListQuery } from '@chukua/common';
import { Button, Popconfirm, Tag, type TableColumnsType } from 'antd';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import { formatInterval } from '../../../policies/interval-labels';
import type { TruckServiceItemView } from '../truck-service.api';

interface BuildTruckPlanColumnsArgs {
  list: Pick<ListQueryControls<TruckServiceItemListQuery>, 'sortOrderFor' | 'filteredValueFor'>;
  /** Without it the actions column is left out: `maintenance.view` alone reads the plan. */
  actions?: {
    onOverride: (row: TruckServiceItemView) => void;
    onRemoveOverride: (row: TruckServiceItemView) => Promise<void>;
  };
}

/** Each column's `key` is its sort field or filter param in `truckServiceItemListQuerySchema`. */
export function buildTruckPlanColumns({ list, actions }: BuildTruckPlanColumnsArgs): TableColumnsType<TruckServiceItemView> {
  const columns: TableColumnsType<TruckServiceItemView> = [
    { title: 'Service item', dataIndex: 'name', key: 'name', sorter: true, sortOrder: list.sortOrderFor('name') },
    { title: 'Interval', key: 'interval', render: (_, row) => formatInterval(row) },
    {
      title: 'Set by',
      dataIndex: 'source',
      key: 'source',
      filters: [
        { text: 'Class plan', value: 'class' },
        { text: 'This truck', value: 'override' },
      ],
      filteredValue: list.filteredValueFor('source'),
      render: (source: TruckServiceItemView['source']) => (
        <Tag color={source === 'override' ? 'warning' : 'default'}>{source === 'override' ? 'This truck' : 'Class plan'}</Tag>
      ),
    },
  ];
  if (actions) {
    columns.push({
      title: '',
      key: 'actions',
      render: (_, row) => (
        <div className="flex gap-2">
          <Button size="small" onClick={() => actions.onOverride(row)}>
            Change for this truck
          </Button>
          {row.source === 'override' && (
            <Popconfirm title={`Make ${row.name} follow the class again?`} onConfirm={() => actions.onRemoveOverride(row)}>
              <Button size="small">Follow class</Button>
            </Popconfirm>
          )}
        </div>
      ),
    });
  }
  return columns;
}
